// Keep browser selection and server publication validation on the same contract.
//
// NOTE: This module is intentionally self-contained and browser-safe. It must NOT
// import across the src/ <-> supabase/ boundary, because the browser build only
// packages the application tree. The server-side publisher uses its own copy at
// supabase/functions/_shared/ebay-active-policies.ts, and both copies must stay
// behaviourally identical (guarded by ebay-active-policies.packaging.test.ts).

export type EbayPolicyType = "fulfillment" | "payment" | "return";

export interface EbayPolicyScope {
  orgId: string;
  connectorId: string;
  marketplaceId: string;
}

export interface ActiveEbayPolicy {
  id: string;
  org_id: string;
  connector_id: string;
  marketplace_id: string;
  policy_type: EbayPolicyType;
  policy_name: string | null;
  external_policy_id: string;
  is_default: boolean;
  is_active: true;
}

export interface EbayPolicyChoices {
  fulfillment: string;
  payment: string;
  return: string;
}

export interface EbayPolicyValidation {
  ok: boolean;
  code: "POLICIES_VALID" | "POLICY_MISSING" | "POLICY_UNAVAILABLE";
  message: string;
  issues: Array<{ code: string; message: string }>;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function policyType(value: unknown): EbayPolicyType | null {
  const kind = text(value).toLowerCase();
  if (kind === "fulfillment" || kind === "fulfilment") return "fulfillment";
  return kind === "payment" || kind === "return" ? kind : null;
}

/** Shared by the listing UI and publisher. History is not publication authority. */
export function activeEbayPolicies(raw: unknown, scope: EbayPolicyScope): ActiveEbayPolicy[] {
  if (!scope.orgId || !scope.connectorId || !scope.marketplaceId || !Array.isArray(raw)) return [];
  const policies: ActiveEbayPolicy[] = [];
  for (const value of raw) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const row = value as Record<string, unknown>;
    const kind = policyType(row.policy_type);
    const externalId = text(row.external_policy_id);
    if (
      row.is_active !== true || !kind || !externalId ||
      text(row.org_id) !== scope.orgId ||
      text(row.connector_id) !== scope.connectorId ||
      text(row.marketplace_id) !== scope.marketplaceId
    ) continue;
    policies.push({
      id: text(row.id),
      org_id: scope.orgId,
      connector_id: scope.connectorId,
      marketplace_id: scope.marketplaceId,
      policy_type: kind,
      policy_name: text(row.policy_name) || null,
      external_policy_id: externalId,
      is_default: row.is_default === true,
      is_active: true,
    });
  }
  return policies;
}

/** Never silently substitute a default for an explicit selection removed by sync. */
export function resolveEbayPolicyId(
  policies: readonly ActiveEbayPolicy[],
  kind: EbayPolicyType,
  selectedId: string,
): string {
  const candidates = policies.filter((p) => p.policy_type === kind && p.is_active === true);
  const selected = text(selectedId);
  if (selected) return candidates.find((p) => p.external_policy_id === selected)?.external_policy_id || "";
  const defaults = candidates.filter((p) => p.is_default);
  return defaults.length === 1 ? defaults[0].external_policy_id : "";
}

/** Type-specific membership must hold even when the active set is empty. */
export function validateEbayPolicyChoices(
  policies: readonly ActiveEbayPolicy[],
  choices: EbayPolicyChoices,
): EbayPolicyValidation {
  const labels: Record<EbayPolicyType, string> = {
    fulfillment: "Postage / fulfillment",
    payment: "Payment",
    return: "Return",
  };
  const issues: Array<{ code: string; message: string }> = [];
  for (const kind of ["fulfillment", "payment", "return"] as const) {
    const selected = text(choices[kind]);
    if (!selected) {
      issues.push({
        code: `${kind.toUpperCase()}_POLICY_MISSING`,
        message: `${labels[kind]} policy is not selected. Select a current policy in the listing's Policies tab.`,
      });
    } else if (!policies.some((p) => p.is_active === true && p.policy_type === kind && p.external_policy_id === selected)) {
      issues.push({
        code: `${kind.toUpperCase()}_POLICY_UNAVAILABLE`,
        message: `${labels[kind]} policy is no longer available for this connector and marketplace. Select a current policy.`,
      });
    }
  }
  if (issues.length === 0) return { ok: true, code: "POLICIES_VALID", message: "", issues: [] };
  return {
    ok: false,
    code: issues.some((issue) => issue.code.endsWith("_UNAVAILABLE")) ? "POLICY_UNAVAILABLE" : "POLICY_MISSING",
    message: issues.map((issue) => issue.message).join(" "),
    issues,
  };
}